import {
  Alert,
  Autocomplete,
  Checkbox,
  Dropdown,
  FormControl,
  FormLabel,
  IconButton,
  ListItemDecorator,
  Menu,
  MenuButton,
  MenuItem,
  Step,
  Stepper,
  Tooltip,
} from '@mui/joy';
import { lowerFirst, pickBy } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { MoreVert } from '@mui/icons-material';
import { Table } from 'ka-table';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import { type FormikValues } from './AddRepairCaseStepper';

import { actions } from '@/reducers/ui';
import { AppContext } from '@/shared/context/context';
import CaseRole from '~/common/enums/caseRole';
import { companyApi } from '@/shared/api';
import { CrmOptionType } from '~/common/enums';
import { customCellWidth } from '@/shared/utils/constants';
import InvolvementType from '~/common/enums/involvementType';
import { trpc } from '@/config/trpc';

import { getInitialPhoneNumbers } from '@/shared/components/2.0/input/FormikMultiPhoneNumbersInput/FormikMultiPhoneNumbers.utils';

import { useCreateCompany, useCreateContact } from '@/mutations';
import useNotification from '@/shared/hooks/UseNotification';
import useToggle from '@/shared/hooks/UseToggle';

import {
  AddIcon,
  CompanyIcon,
  ContactIcon,
  DeleteIcon,
  InfoIcon,
  InlineSpinner,
  InsuredCaseIcon,
  InsurerIcon,
  TeamIcon,
  WarningIcon,
} from '@/shared/icons/Icons';
import AddEditCompanyForm from '@/containers/Companies/AddEditCompany/AddEditCompanyForm';
import AddEditContactForm from '@/containers/Contacts/AddEditContact/AddEditContactForm';
import { type DuplicateCase } from '@/shared/utils/duplicateCase';
import DuplicateReferenceWarning from '@/shared/components/2.0/DuplicateReferenceWarning';
import HeaderStep from './HeaderStep';
import InfoModal from './InfoModal';
import InvolvedPartyReferenceCell from './InvolvedPartyReferenceCell';
import KpcModal from '@/shared/components/2.0/layout/KpcModal';
import StepIndicator from './StepIndicator';
import VirtualListBoxAdapter from '@/shared/components/2.0/adapters/VirtualListBoxAdapter';

type Party = {
  value?: string | null;
  label: string;
  type: CrmOptionType;
  entityId?: string | null;
  isTeam?: boolean;
};

const InvolvedPartiesStep = () => {
  const { ui, setUi, currentTeam } = useContext(AppContext);
  const { values, setFieldValue } = useFormikContext<FormikValues>();
  const [selectedParty, setSelectedParty] = useState<Party | null>(null);
  const [duplicateReferenceCasesByRelation, setDuplicateReferenceCasesByRelation] = useState<{
    [relationId: string]: DuplicateCase[];
  }>({});
  const { sendNotification } = useNotification();

  const { t } = useTranslation();

  const companyToggle = useToggle();
  const contactToggle = useToggle();

  const {
    data: crmOptions,
    isLoading: crmIsLoading,
    refetch: refetchCrmOptions,
  } = trpc.contact.involvedParties.useQuery();

  const { data: contact, error: contactError } = trpc.contact.byId.useQuery(
    selectedParty?.entityId as string,
    {
      enabled: !!(selectedParty?.type === CrmOptionType.Contact),
    },
  );

  const { data: company, error: companyError } = trpc.company.byId.useQuery(
    selectedParty?.entityId as string,
    { enabled: !!(selectedParty?.type === CrmOptionType.Company) },
  );

  const { mutateAsync: createCompanyAsync } = useCreateCompany();
  const { mutateAsync: createContactAsync } = useCreateContact();

  const involvedParties = useMemo(() => {
    if (!crmOptions) return [];

    if (values.relations.length > 0) {
      return crmOptions.filter(
        (option) => !values.relations.some((relation) => relation.id === option.value),
      );
    }
    return crmOptions;
  }, [values.relations, crmOptions]);

  const handleDuplicateReferenceCasesChange = (relationId: string, cases: DuplicateCase[]) => {
    setDuplicateReferenceCasesByRelation((previous) => ({ ...previous, [relationId]: cases }));
  };

  useEffect(() => {
    const relationIds = new Set(values.relations.map((relation) => relation.id));
    setDuplicateReferenceCasesByRelation((previous) => {
      const next = pickBy(previous, (_cases, relationId) => relationIds.has(relationId));
      return Object.keys(next).length === Object.keys(previous).length ? previous : next;
    });
  }, [values.relations]);

  const duplicateReferenceCases = useMemo(() => {
    const casesById = new Map<string, DuplicateCase>();
    Object.values(duplicateReferenceCasesByRelation).forEach((relationCases) => {
      relationCases.forEach((duplicateCase) => casesById.set(duplicateCase.id, duplicateCase));
    });
    return Array.from(casesById.values());
  }, [duplicateReferenceCasesByRelation]);

  const caseRoles = Object.values(CaseRole).map((role) => ({
    label: t(lowerFirst(role)),
    value: role,
  }));

  const involvementTypes = Object.values(InvolvementType).map((involvementType) => ({
    label: t(lowerFirst(involvementType)),
    value: involvementType,
  }));

  const hasRequestor = values.relations.some((relation) => relation.isRequestor);
  const hasClient = values.relations.some((relation) => relation.isClient);
  const hasInsurer = values.relations.some((relation) => relation.role === CaseRole.Insurer);
  const hasIntermediary = values.relations.some(
    (relation) => relation.role === CaseRole.InsuranceIntermediary,
  );

  const isValidInvolvedParties = currentTeam?.allowRepairManagement
    ? hasRequestor && hasClient && hasInsurer && hasIntermediary
    : hasRequestor && hasClient && hasIntermediary;

  const defaultValues = {
    involvementType: null,
    isClient: false,
    isRequestor: false,
    relationReference: '',
    role: contact?.defaultCaseRole || company?.defaultCaseRole || null,
  };

  return (
    <>
      <HeaderStep
        description={t('repairCaseForm.involvedParties.description')}
        title={t('repairCaseForm.involvedParties.title')}
        tooltipMessage={t('repairCaseForm.involvedParties.tooltip')}
      />

      {(contactError || companyError) && (
        <Alert color="danger" startDecorator={<WarningIcon />}>
          {t('repairCaseForm.involvedParties.error')}
        </Alert>
      )}

      {values.relations.some((relation) => relation.hasMultipleOptions) && (
        <Alert color="warning" startDecorator={<WarningIcon />}>
          {t('repairCaseForm.involvedParties.teamPrefillWarning')}
        </Alert>
      )}

      {!!duplicateReferenceCases.length && (
        <DuplicateReferenceWarning cases={duplicateReferenceCases} />
      )}

      <div
        className={classNames('p-4', 'rounded-xl border', {
          'bg-gray-50': !isValidInvolvedParties,
          'bg-green-100': isValidInvolvedParties,
        })}
      >
        <Stepper>
          <Step indicator={<StepIndicator required permission={hasRequestor} />}>
            {t('repairCaseForm.involvedParties.validation.requestor')}
          </Step>
          <Step indicator={<StepIndicator required permission={hasClient} />}>
            {t('repairCaseForm.involvedParties.validation.client')}
          </Step>
          <Step indicator={<StepIndicator permission={hasInsurer} />}>
            {t('repairCaseForm.involvedParties.validation.insurer')}
          </Step>
          <Step indicator={<StepIndicator permission={hasIntermediary} />}>
            {t('repairCaseForm.involvedParties.validation.intermediary')}
          </Step>
        </Stepper>
      </div>
      <FormControl>
        <FormLabel>{t('involvedParty')}</FormLabel>
        <div className="flex space-x-4">
          <Autocomplete
            className="grow"
            disabled={crmIsLoading}
            groupBy={(option) => option.type}
            options={involvedParties || []}
            placeholder={t('typeHere')}
            renderGroup={(params) => params as unknown as React.ReactNode}
            renderOption={(props, option) => [props, option] as React.ReactNode}
            slots={{ listbox: VirtualListBoxAdapter }}
            startDecorator={crmIsLoading && <InlineSpinner />}
            value={selectedParty}
            onChange={(_e, party) => {
              if (party) {
                setSelectedParty(party);
              }
            }}
          />
          <IconButton
            disabled={!selectedParty}
            onClick={() => {
              if (!selectedParty) return;

              if (contact && selectedParty.type === CrmOptionType.Contact) {
                const { phoneNumber, mobilePhoneNumber, ...contactData } = contact;

                setFieldValue('relations', [
                  ...values.relations,
                  {
                    ...defaultValues,
                    ...contactData,
                    ...selectedParty,
                    companyId: null,
                    contactId: selectedParty.value,
                    emailAddresses: contact.emailAddresses.map((e) => e.email),
                    language: contact.language,
                    teamId: null,
                  },
                ]);
              }
              if (company && selectedParty.type === CrmOptionType.Company) {
                const { phoneNumber, mobilePhoneNumber, ...companyData } = company;

                setFieldValue('relations', [
                  ...values.relations,
                  {
                    ...defaultValues,
                    ...companyData,
                    ...selectedParty,
                    companyId: selectedParty.value,
                    contactId: null,
                    emailAddresses: company.emailAddresses.map((e) => e.email),
                    language: company.language,
                    teamId: null,
                  },
                ]);
              }
              if (company && selectedParty.type === CrmOptionType.Company && selectedParty.isTeam) {
                const { phoneNumber, mobilePhoneNumber, ...companyData } = company;

                setFieldValue('relations', [
                  ...values.relations,
                  {
                    ...defaultValues,
                    ...companyData,
                    ...selectedParty,
                    companyId: null,
                    contactId: null,
                    emailAddresses: company.emailAddresses.map((e) => e.email),
                    id: selectedParty.value,
                    language: company.language,
                    teamId: selectedParty.value,
                  },
                ]);
              }

              setSelectedParty(null);
            }}
          >
            <AddIcon />
          </IconButton>
          <Dropdown>
            <MenuButton slots={{ root: IconButton }}>
              <MoreVert />
            </MenuButton>
            <Menu className="z-popperModal" placement="bottom-end">
              <MenuItem onClick={() => contactToggle.show()}>
                <ListItemDecorator>
                  <ContactIcon />
                </ListItemDecorator>
                {t('addContact')}
              </MenuItem>
              <MenuItem onClick={() => companyToggle.show()}>
                <ListItemDecorator>
                  <CompanyIcon />
                </ListItemDecorator>
                {t('addCompany')}
              </MenuItem>
            </Menu>
          </Dropdown>
        </div>
      </FormControl>

      <div className="rounded-xl border">
        <Table
          childComponents={{
            cellText: {
              // eslint-disable-next-line react/no-unstable-nested-components
              content: (props) => {
                switch (props.column.key) {
                  case 'type': {
                    if (props.rowData.role === CaseRole.Insurer) {
                      return (
                        <Tooltip title={t('insurer')}>
                          <span>
                            <InsurerIcon />
                          </span>
                        </Tooltip>
                      );
                    }
                    if (props.rowData.role === CaseRole.InsuranceIntermediary) {
                      return (
                        <Tooltip title={t('intermediary')}>
                          <span>
                            <InsuredCaseIcon />
                          </span>
                        </Tooltip>
                      );
                    }
                    if (props.rowData.type === CrmOptionType.Contact) {
                      return (
                        <Tooltip title={t('contact')}>
                          <span>
                            <ContactIcon />
                          </span>
                        </Tooltip>
                      );
                    }
                    if (props.rowData.type === CrmOptionType.Team) {
                      return (
                        <Tooltip title={t('team')}>
                          <span>
                            <TeamIcon />
                          </span>
                        </Tooltip>
                      );
                    }
                    return (
                      <Tooltip title={t('company')}>
                        <span>
                          <CompanyIcon />
                        </span>
                      </Tooltip>
                    );
                  }
                  case 'label': {
                    const options =
                      crmOptions?.filter(
                        (opt) => props.rowData.centralCompanyId === opt.centralCompanyId,
                      ) || [];
                    if (!options.length) return props.value;

                    return options.length > 1 && props.rowData.isTeam ? (
                      <Autocomplete
                        disableClearable
                        options={options}
                        value={options.find((opt) => opt.value === props.rowData.teamId)}
                        onBlur={() =>
                          setFieldValue(
                            'relations',
                            values.relations.map((relation) => {
                              if (relation.id === props.rowData.id) {
                                return {
                                  ...relation,
                                  hasMultipleOptions: false,
                                };
                              }
                              return relation;
                            }),
                          )
                        }
                        onChange={(_, option) => {
                          setFieldValue(
                            'relations',
                            values.relations.map((relation) => {
                              if (relation.id === props.rowData.id) {
                                return {
                                  ...relation,
                                  hasMultipleOptions: false,
                                  teamId: option?.value,
                                };
                              }
                              return relation;
                            }),
                          );
                        }}
                      />
                    ) : (
                      props.value
                    );
                  }
                  case 'isRequestor':
                    return (
                      <Checkbox
                        checked={props.rowData.isRequestor}
                        onChange={(e) => {
                          setFieldValue(
                            'relations',
                            values.relations.map((relation) => {
                              if (relation.id === props.rowData.id) {
                                return { ...relation, isRequestor: e.target.checked };
                              }
                              return { ...relation, isRequestor: false };
                            }),
                          );
                        }}
                      />
                    );
                  case 'isClient':
                    return (
                      <Checkbox
                        checked={props.rowData.isClient}
                        onChange={(e) => {
                          setFieldValue(
                            'relations',
                            values.relations.map((relation) => {
                              if (relation.id === props.rowData.id) {
                                return { ...relation, isClient: e.target.checked };
                              }
                              return relation;
                            }),
                          );
                        }}
                      />
                    );
                  case 'role':
                    return (
                      <Autocomplete
                        options={caseRoles}
                        placeholder={t('typeHere')}
                        value={caseRoles.find((role) => role.value === props.value) || null}
                        onChange={(_e, role) => {
                          setFieldValue(
                            'relations',
                            values.relations.map((relation) => {
                              if (relation.id === props.rowData.id) {
                                return { ...relation, role: role?.value };
                              }
                              return relation;
                            }),
                          );
                        }}
                      />
                    );

                  case 'involvementType':
                    return (
                      <Autocomplete
                        options={involvementTypes}
                        placeholder={t('typeHere')}
                        value={
                          involvementTypes.find(
                            (involvementType) => involvementType.value === props.value,
                          ) || null
                        }
                        onChange={(_event, involvementType) => {
                          setFieldValue(
                            'relations',
                            values.relations.map((relation) => {
                              if (relation.id === props.rowData.id) {
                                return { ...relation, involvementType: involvementType?.value };
                              }
                              return relation;
                            }),
                          );
                        }}
                      />
                    );
                  case 'relationReference':
                    return (
                      <InvolvedPartyReferenceCell
                        reference={props.value}
                        role={props.rowData.role}
                        onChange={(relationReference) => {
                          setFieldValue(
                            'relations',
                            values.relations.map((relation) => {
                              if (relation.id === props.rowData.id) {
                                return { ...relation, relationReference };
                              }
                              return relation;
                            }),
                          );
                        }}
                        onDuplicateCasesChange={(cases) =>
                          handleDuplicateReferenceCasesChange(props.rowData.id, cases)
                        }
                      />
                    );

                  case 'id':
                    return (
                      <div className="flex space-x-2">
                        <IconButton
                          onClick={() => {
                            setUi({
                              payload: props.rowData.id,
                              type: actions.CASE_TOGGLE_INVOLVED_PARTY_INFO,
                            });
                          }}
                        >
                          <InfoIcon />
                        </IconButton>
                        <IconButton
                          color="danger"
                          onClick={() => {
                            setFieldValue(
                              'relations',
                              values.relations.filter(
                                (relation) => relation.id !== props.rowData.id,
                              ),
                            );
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    );

                  default:
                    return props.value;
                }
              },
            },

            noDataRow: { content: () => t('noDataToDisplay') },
          }}
          columns={[
            {
              key: 'type',
              title: '',
              width: 50,
            },
            {
              key: 'label',
              title: t('name'),
            },
            {
              key: 'isRequestor',
              title: t('requestor'),
              width: customCellWidth.S,
            },
            {
              key: 'isClient',
              title: t('client'),
              width: customCellWidth.S,
            },
            {
              key: 'role',
              title: t('role'),
            },
            {
              key: 'involvementType',
              title: t('involvementType'),
            },
            {
              key: 'relationReference',
              title: t('reference'),
            },
            {
              key: 'id',
              title: t('actions'),
              width: customCellWidth.SM,
            },
          ]}
          data={values.relations}
          rowKeyField="id"
        />
      </div>

      {ui.case.toggles.involvedPartyInfo && <InfoModal />}

      {companyToggle.value && (
        <KpcModal title={t('company')} toggle={companyToggle}>
          <AddEditCompanyForm
            onCancel={companyToggle.hide}
            onSubmit={async (data) => {
              try {
                const createdCompany = await createCompanyAsync(data);
                const companyTeamsResponse = await companyApi.getCompanyTeams();

                const [foundCompanyTeam = null] =
                  companyTeamsResponse.find((companyTeams) => companyTeams.id === createdCompany.id)
                    ?.teams ?? [];

                const emailAddresses = createdCompany.emailAddresses.map(
                  (emailAddress) => emailAddress.email,
                );

                companyToggle.hide();
                refetchCrmOptions();
                setFieldValue('relations', [
                  ...values.relations,
                  {
                    ...defaultValues,
                    address: null,
                    companyAddresses: createdCompany.companyAddresses,
                    companyId: foundCompanyTeam ? null : createdCompany.id,
                    companyName: createdCompany.companyName ?? null,
                    contactId: null,
                    emailAddresses,
                    entityId: createdCompany.id,
                    firstName: null,
                    gender: null,
                    id: createdCompany.id,
                    isTeam: !!foundCompanyTeam,
                    label: createdCompany.displayName,
                    language: createdCompany.language,
                    lastName: null,
                    phoneNumbers: getInitialPhoneNumbers(createdCompany.phoneNumbers) ?? [],
                    remark: null,
                    role: createdCompany.defaultCaseRole ?? defaultValues.role,
                    teamId: foundCompanyTeam ? foundCompanyTeam.teamId : null,
                    type: CrmOptionType.Company,
                    value: createdCompany.id,
                    vatNumber: createdCompany.vatNumber ?? null,
                  },
                ]);
              } catch (error) {
                if (!!error && typeof error === 'object' && 'message' in error)
                  sendNotification({
                    message: t('errorCreatingCompany', { error: error.message }),
                    variant: 'error',
                  });
              }
            }}
          />
        </KpcModal>
      )}

      {contactToggle.value && (
        <KpcModal title={t('contact')} toggle={contactToggle}>
          <AddEditContactForm
            onCancel={contactToggle.hide}
            onSubmit={async (contactValues) => {
              const { addressLine, city, country, postalCode, ...rest } = contactValues;

              const contactResponse = await createContactAsync({
                ...rest,
                address: {
                  addressLine,
                  city,
                  country,
                  postalCode,
                },
              });
              refetchCrmOptions();
              contactToggle.hide();

              const { mobilePhoneNumber, phoneNumber, ...contactData } = contactResponse;

              setFieldValue('relations', [
                ...values.relations,
                {
                  ...defaultValues,
                  ...contactData,
                  companyId: null,
                  contactId: contactResponse.id,
                  emailAddresses: contactResponse.emailAddresses.map((e) => e.email),
                  entityId: contactResponse.id,
                  label: `${contactResponse.firstName ?? ''} ${contactResponse.lastName}` || '',
                  language: contactResponse.language,
                  phoneNumbers: getInitialPhoneNumbers(contactResponse.phoneNumbers) ?? [],
                  role: contactResponse.defaultCaseRole || null,
                  teamId: null,
                  type: CrmOptionType.Contact,
                  value: contactResponse.id,
                },
              ]);
            }}
          />
        </KpcModal>
      )}
    </>
  );
};

export default InvolvedPartiesStep;
